import { toRaw } from "vue";
const batchSize = 200;
const yieldToPage = () => new Promise(resolve => setTimeout(resolve, 0));
const canceled = () => Object.assign(new Error("Export canceled"), { code: "ERR_CANCELED" });
// A nested Vue proxy cannot be structured-cloned. Unwrap each small batch rather
// than JSON-stringifying the entire reactive report on the browser's UI thread.
function snapshot(value, seen = new WeakMap()) {
    if (value === null || typeof value !== "object")
        return value;
    const raw = toRaw(value);
    if (raw instanceof Date)
        return new Date(raw.getTime());
    if (seen.has(raw))
        return seen.get(raw);
    const result = Array.isArray(raw) ? [] : {};
    seen.set(raw, result);
    for (const key of Object.keys(raw))
        result[key] = snapshot(raw[key], seen);
    return result;
}
function defaultWorker() {
    return new Worker(new URL("../workers/reportExport.worker.ts", import.meta.url));
}
// The injectable factory also lets local tests run the actual worker code.
export function createExportWorker(createWorker = defaultWorker) {
    function run(start, sources, signal) {
        return new Promise((resolve, reject) => {
            let worker;
            let settled = false;
            const cleanup = () => {
                signal?.removeEventListener("abort", abort);
                worker?.terminate();
            };
            const fail = (error) => {
                if (settled)
                    return;
                settled = true;
                cleanup();
                reject(error);
            };
            const abort = () => fail(canceled());
            signal?.addEventListener("abort", abort, { once: true });
            if (signal?.aborted) {
                abort();
                return;
            }
            const send = async () => {
                // Allow the loading indicator and pending user input to paint first.
                await yieldToPage();
                if (settled)
                    return;
                worker = createWorker();
                worker.onerror = () => fail(new Error("Export worker failed"));
                worker.onmessageerror = () => fail(new Error("Export worker response could not be read"));
                worker.onmessage = ({ data }) => {
                    if (settled)
                        return;
                    if (data?.type !== "complete" || !(data.buffer instanceof ArrayBuffer)) {
                        fail(new Error("Export data could not be prepared"));
                        return;
                    }
                    const body = new Blob([data.buffer], { type: "application/json" });
                    settled = true;
                    cleanup();
                    resolve(body);
                };
                worker.postMessage(snapshot(start));
                for (let sheetIndex = 0; sheetIndex < sources.length; sheetIndex++) {
                    const rows = toRaw(sources[sheetIndex]);
                    let offset = 0;
                    while (offset < rows.length) {
                        if (settled)
                            return;
                        const batch = [];
                        const began = performance.now();
                        while (offset < rows.length && batch.length < batchSize) {
                            batch.push(snapshot(rows[offset++]));
                            if (performance.now() - began >= 8)
                                break;
                        }
                        worker.postMessage({ type: "rows", sheetIndex, rows: batch });
                        await yieldToPage();
                    }
                }
                if (!settled)
                    worker.postMessage({ type: "finish" });
            };
            send().catch(fail);
        });
    }
    return {
        prepareDashboardExport(input, signal) {
            const { results, ...metadata } = input;
            return run({ type: "start", kind: "dashboard", input: metadata }, [results || []], signal);
        },
        serializeExportSheets(sheets, signal) {
            return run({ type: "start", kind: "sheets", sheets: sheets.map(({ rows, ...sheet }) => sheet) }, sheets.map(sheet => sheet.rows || []), signal);
        }
    };
}
const exporter = createExportWorker();
export const prepareDashboardExport = exporter.prepareDashboardExport;
export const serializeExportSheets = exporter.serializeExportSheets;
